/*
  An optimized experience for entering a numeric code (e.g. OTP).

  <drb-code-input name="otp" digits="6"></drb-code-input>
*/

import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import styles from './drb-code-input.scss?inline';
import { onNextRepaint } from '~/shared/utils/animation';

@customElement('drb-code-input')
class DrbCodeInput extends LitElement {
  static styles = unsafeCSS(styles);
  form?: HTMLFormElement = this.closest('form');

  @property({ type: String })
  name = 'otp';

  @property({ type: Number })
  digits = 6;

  @property({ type: Boolean, reflect: true })
  disabled = false;

  @property({ attribute: 'auto-focus', type: Boolean })
  autoFocus = false;

  @property()
  get value(): string {
    return this.getInputs().map(input => input.value).join('');
  }

  set value(newCode: string) {
    const sanitizedCode = newCode.replace(/\D/g, '').slice(0, this.digits) || '';

    this.getInputs().forEach((input, index) => {
      input.value = sanitizedCode[index] || '';
    });

    this.focus();
    this.checkCodeCompleted();
  }

  connectedCallback() {
    super.connectedCallback();

    // connect the value to form data (if needed)
    if (this.form && this.name) {
      this.form.addEventListener('formdata', ({ formData }) => {
        formData.append(this.name, this.value);
      });
    }

    // Focus on mount if `autoFocus` is true
    if (this.autoFocus) {
      onNextRepaint(() => {
        this.focus();
      });
    }
  }

  focus() {
    // Focus and select the first empty input or the last input
    const inputs = this.getInputs();
    const firstEmptyInput = inputs.find(input => !input.value);
    const lastInput = inputs[inputs.length - 1];

    const targetInput = firstEmptyInput || lastInput;

    // Prevent tab focus on other inputs (and avoid tab focus being trapped)
    inputs.forEach(input => {
      input.tabIndex = input === targetInput ? 0 : -1;
    });

    targetInput?.focus();

    // Move selection to end in the event user clicks on beginning of input for better UX
    setTimeout(() => {
      targetInput?.setSelectionRange(1, 1);
    }, 0);
  }

  private getInputs = () => {
    return Array.from(this.shadowRoot?.querySelectorAll('input'));
  }

  private handleInput = (e) => {
    const input = e.target as HTMLInputElement;

    // support 2fa auto-fill from password manager extensions
    if (input.value.length > 1) {
      this.value = input.value;
      e.preventDefault();
      return;
    }

    this.focus();
    this.checkCodeCompleted();
  }

  private handlePaste(e: ClipboardEvent) {
    e.preventDefault();
    const pastedString = e.clipboardData?.getData('text') || '';
    this.value = pastedString;
  }

  private handleKeydown(e: KeyboardEvent)  {
    // Allow navigation keys, delete, & pasting
    if (
      e.key === 'Delete' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight' ||
      e.key === 'Tab' ||
      e.ctrlKey || e.metaKey
    ) {
      return;
    }

    // Clear last "non-empty" input on backspace
    if (e.key === 'Backspace') {
      e.preventDefault();
      const lastNonEmptyInput = this.getInputs().reverse().find(input => input.value);

      if (lastNonEmptyInput) {
        lastNonEmptyInput.value = '';
      }

      this.focus();
      return;
    }

    // Prevent non-numeric input
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  }

  private checkCodeCompleted() {
    if (this.value.length !== this.digits) return;

    this.blur();

    this.dispatchEvent(new CustomEvent('drb-code-input:complete', {
      bubbles: true,
      composed: true,
      detail: { code: this.value }
    }));
  }

  render() {
    return html`
      <div class="code-input">
        ${[...Array(this.digits)].map((_, index) => html`
          <input
            type="text"
            maxlength="1"
            autocomplete="${index === 0 ? 'one-time-code' : 'off'}"
            inputmode="numeric"
            placeholder=""
            aria-label="Code digit ${index + 1} of ${this.digits}"
            tabindex="${index === 0 ? 0 : -1}"
            ?disabled=${this.disabled}
            @input=${this.handleInput}
            @keydown=${this.handleKeydown}
            @paste=${this.handlePaste}
            @focus=${this.focus}
          />
        `)}
      </div>
    `;
  }
}

export { DrbCodeInput };
